import { useState } from "react";
import { toast } from "sonner";
import { Sprout, MapPin, Droplets, Lightbulb, ShieldCheck, LocateFixed, Search, Compass, Handshake, Sparkles } from "lucide-react";
import Navbar from "../components/Navbar";
import PurchaseWizard from "../components/PurchaseWizard";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

function WaterBadge({ score }) {
  const s = Number(score) || 0;
  const color = s >= 7 ? "text-moss bg-moss-bg/60 border-moss/30" : s >= 5 ? "text-amber_brand bg-amber_brand-bg border-amber_brand/30" : "text-terracotta bg-terracotta-bg border-terracotta/30";
  return (
    <span className={`inline-flex items-center gap-1 text-[0.6rem] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${color}`}>
      <Droplets className="w-3 h-3" /> water {s}/10
    </span>
  );
}

function OutsiderBadge({ value, note }) {
  if (!value) return null;
  const v = String(value).toLowerCase();
  const cls = v === "yes" ? "text-moss bg-moss-bg/60 border-moss/30" : v === "no" ? "text-terracotta bg-terracotta-bg border-terracotta/30" : "text-amber_brand bg-amber_brand-bg border-amber_brand/30";
  return (
    <span className={`inline-flex items-center text-[0.6rem] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${cls}`} title={note || ""}>
      Outsider friendly: {value}
    </span>
  );
}

const FEATURED_REGIONS = [
  { label: "Nagpur outer belt, MH", query: "Nagpur", tag: "Logistics corridor · ₹4-8L/acre" },
  { label: "Chikkaballapur, KA", query: "Chikkaballapur Karnataka", tag: "1hr from Bengaluru · borewell belt" },
  { label: "Nimar belt, MP", query: "Khargone Madhya Pradesh", tag: "Narmada canal · ₹3-6L/acre" },
  { label: "Warangal, TS", query: "Warangal Telangana", tag: "Tank-fed · outsider friendly" },
  { label: "Dausa–Jaipur, RJ", query: "Dausa Rajasthan", tag: "Cheap dryland + drip potential" },
  { label: "Alentejo, Portugal", query: "Alentejo Portugal", tag: "EU farmland · $3-6k/acre" },
];

export default function Land() {
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [guide, setGuide] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [leadSending, setLeadSending] = useState(false);
  const [budget, setBudget] = useState("");
  const [deals, setDeals] = useState(null);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [refining, setRefining] = useState(false);

  const fetchDeals = async (locOverride) => {
    const loc = locOverride || guide?.location_resolved || location;
    if (!loc || dealsLoading) return;
    setDealsLoading(true);
    setDeals(null);
    try {
      // Fast curated path — returns in < 500ms
      const { data } = await api.get(
        `/land/deals?location=${encodeURIComponent(loc)}${budget ? `&budget=${encodeURIComponent(budget)}` : ""}`,
        { timeout: 15000 },
      );
      setDeals(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deals lookup failed");
    } finally {
      setDealsLoading(false);
    }
  };

  const refineDeals = async () => {
    const loc = deals?.location || guide?.location_resolved || location;
    if (!loc || refining) return;
    setRefining(true);
    try {
      const { data } = await api.get(
        `/land/deals/refine?location=${encodeURIComponent(loc)}${budget ? `&budget=${encodeURIComponent(budget)}` : ""}`,
        { timeout: 120000 },
      );
      setDeals(data);
      toast.success("Refined with live AI research");
    } catch {
      toast.error("AI refine timed out — curated deals below are still accurate.");
    } finally {
      setRefining(false);
    }
  };

  const sendLead = async () => {
    if (leadSending) return;
    setLeadSending(true);
    try {
      await api.post("/leads", { type: "land", location: guide?.location_resolved || location || null, note: "land-results-connect" });
      toast.success("Request received — we'll connect you with verified land sellers");
    } catch {
      toast.error("Could not send — try again");
    } finally {
      setLeadSending(false);
    }
  };

  const fetchGuide = async (payload) => {
    setLoading(true);
    setGuide(null);
    // Fire the FAST curated deals immediately in parallel — user sees results in <1s,
    // even if the LLM guide takes 20-90s or fails entirely.
    if (payload?.location) fetchDeals(payload.location);
    try {
      const { data } = await api.post("/land/guide", payload, { timeout: 120000 });
      setGuide(data);
      // If deals hasn't loaded yet (e.g. user used geolocation), fire it now with resolved name
      if (!deals && data?.location_resolved) fetchDeals(data.location_resolved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Guide unavailable — deals below still work");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!location.trim() || loading) return;
    fetchGuide({ location: location.trim() });
  };

  const handleMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location not supported on this device — type a place instead");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchGuide({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => toast.error("Couldn't get your location — type a place instead"),
      { timeout: 10000 }
    );
  };

  return (
    <div className="min-h-screen bg-bone-50" data-testid="land-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-8">
          <div className="overline mb-3 flex items-center gap-1.5">
            <Sprout className="w-3.5 h-3.5" />— Farmland Radar · India + World
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            Cheap agri land, with water
          </h1>
          <p className="text-stone_brand mt-2 max-w-2xl">
            Pick a location (or use yours) — get the cheapest farmland-with-water regions nearby,
            typical ₹/acre ranges, and out-of-the-box ways to actually get it.
          </p>
        </div>

        <form onSubmit={handleSearch} className="bg-white border border-line rounded-sm p-5 mb-8 flex flex-wrap items-center gap-2" data-testid="land-search-form">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone_brand-light" />
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Bengaluru · Nagpur · Texas · Portugal"
              className="pl-9 rounded-sm border-line bg-white h-11 focus-visible:ring-moss"
              data-testid="land-location-input"
            />
          </div>
          <Button type="submit" disabled={loading || !location.trim()} className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-11 px-5 gap-1.5" data-testid="land-search-btn">
            <Search className="w-4 h-4" /> {loading ? "Researching…" : "Find land"}
          </Button>
          <Button type="button" variant="outline" onClick={handleMyLocation} disabled={loading} className="rounded-sm border-moss text-moss hover:bg-moss-bg h-11 px-4 gap-1.5" data-testid="land-my-location-btn">
            <LocateFixed className="w-4 h-4" /> Use my location
          </Button>
          <p className="basis-full text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light">
            — AI research guidance · indicative price ranges · always verify title, water &amp; local law on the ground
          </p>
        </form>

        {/* Fast curated deals — shown as soon as search fires, independent of the slow LLM guide */}
        {(deals || dealsLoading) && (
          <section className="mb-8" data-testid="land-deals-section">
            <div className="overline mb-3 flex items-center gap-1.5 text-moss">
              <Search className="w-3.5 h-3.5" />— Land deals in your budget · linkable results
            </div>
            <div className="bg-white border border-line rounded-sm p-4 flex flex-wrap items-center gap-2 mb-3">
              <Input
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="Budget · e.g. under ₹20 lakh total, or ₹5L/acre"
                className="rounded-sm border-line bg-white h-10 max-w-sm focus-visible:ring-moss"
                data-testid="land-budget-input"
              />
              <Button onClick={() => fetchDeals()} disabled={dealsLoading} className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-10 gap-1.5" data-testid="land-deals-btn">
                <Search className="w-4 h-4" /> {dealsLoading ? "Loading…" : "Find deals"}
              </Button>
              {deals && (
                <Button
                  onClick={refineDeals}
                  disabled={refining}
                  variant="outline"
                  className="rounded-sm border-line text-stone_brand-dark hover:border-moss hover:text-moss h-10 gap-1.5"
                  data-testid="land-deals-refine-btn"
                  title="Ask AI for the latest market patterns for this location — takes 30-90s"
                >
                  <Sparkles className="w-4 h-4" /> {refining ? "Refining with AI…" : "Refine with AI"}
                </Button>
              )}
            </div>
            {dealsLoading && !deals && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3" data-testid="land-deals-skeleton">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-white border border-line rounded-sm p-4 animate-pulse">
                    <div className="h-4 bg-bone rounded w-3/4 mb-2" />
                    <div className="h-3 bg-bone rounded w-1/2 mb-2" />
                    <div className="h-3 bg-bone rounded w-2/3" />
                  </div>
                ))}
              </div>
            )}
            {deals && (
              <div className="space-y-3" data-testid="land-deals-results">
                {refining && (
                  <div className="bg-moss-bg/40 border border-moss/30 rounded-sm p-3 text-xs text-stone_brand-dark flex items-center gap-2" data-testid="land-refining-indicator">
                    <div className="w-3 h-3 border-2 border-moss border-t-transparent rounded-full animate-spin" />
                    Asking Claude for market-current patterns for {deals.location}…
                  </div>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
                  {(deals.portals || []).map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer" className="bg-white border border-line rounded-sm p-3 hover:border-moss/60 transition-colors group" data-testid={`deal-portal-${i}`}>
                      <div className="text-xs font-semibold text-stone_brand-dark group-hover:text-moss flex items-center gap-1">{p.portal} <span className="text-stone_brand-light">↗</span></div>
                      <div className="text-[0.65rem] text-stone_brand mt-0.5">{p.label}</div>
                    </a>
                  ))}
                </div>
                {(deals.indicative_deals || []).length > 0 && (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(deals.indicative_deals || []).map((d, i) => (
                      <div key={i} className="bg-white border border-line rounded-sm p-4" data-testid={`deal-card-${i}`}>
                        <div className="text-sm font-semibold text-stone_brand-dark mb-0.5">{d.title}</div>
                        <div className="font-mono text-sm text-moss font-semibold mb-1">{d.price_indicative}</div>
                        <p className="text-[0.7rem] text-stone_brand mb-0.5 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{d.locality} · {d.area_hint}</span></p>
                        <p className="text-[0.7rem] text-stone_brand mb-0.5 flex items-start gap-1"><Droplets className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{d.water}</span></p>
                        <p className="text-[0.7rem] text-stone_brand mb-1">{d.why_good}</p>
                        <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1.5 py-0.5 rounded-sm">find on: {d.search_on}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light">
                  — {deals.note}
                  {deals.source === "curated" && (
                    <span className="text-moss ml-2">· curated · tap &ldquo;Refine with AI&rdquo; for market-current patterns</span>
                  )}
                  {deals.source === "ai_enriched" && (
                    <span className="text-amber_brand ml-2">· AI-refined</span>
                  )}
                </p>
              </div>
            )}
          </section>
        )}

        {loading && (
          <div className="bg-white border border-dashed border-line rounded-sm p-6 text-center mb-8" data-testid="land-guide-loading">
            <div className="font-serif-display text-base text-stone_brand-dark animate-pulse">Deep-researching this region (regions, water tables, legal notes)…</div>
            <p className="text-xs text-stone_brand mt-1">Takes ~15-25 seconds · deals above are ready to browse in the meantime</p>
          </div>
        )}

        {guide && !loading && (
          <div className="space-y-8" data-testid="land-results">
            <div className="bg-white border border-line rounded-sm p-5">
              <div className="overline mb-1 text-moss">— {guide.location_resolved}</div>
              <p className="text-sm text-stone_brand">{guide.summary}</p>
            </div>

            <section>
              <div className="overline mb-3 flex items-center gap-1.5 text-moss">
                <MapPin className="w-3.5 h-3.5" />— Cheapest regions with water
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {(guide.cheapest_regions || []).map((r, i) => (
                  <div key={i} className="bg-white border border-line rounded-sm p-5" data-testid={`land-region-${i}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div>
                        <div className="font-serif-display text-lg text-stone_brand-dark font-medium leading-tight">{r.region}</div>
                        <div className="text-[0.7rem] font-mono uppercase tracking-widest text-stone_brand-light">{r.state_or_country}</div>
                      </div>
                      <WaterBadge score={r.water_score} />
                    </div>
                    <div className="mb-1.5"><OutsiderBadge value={r.outsider_friendly} note={r.outsider_note} /></div>
                    <div className="font-mono text-base text-moss font-semibold mb-1.5">{r.typical_price_per_acre}</div>
                    <p className="text-xs text-stone_brand mb-1"><span className="font-semibold">Water:</span> {r.water_availability}</p>
                    <p className="text-xs text-stone_brand mb-1"><span className="font-semibold">Why cheap:</span> {r.why_cheap}</p>
                    <p className="text-[0.7rem] font-mono text-stone_brand-light">{r.distance_note}</p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <div className="overline mb-3 flex items-center gap-1.5 text-amber_brand">
                <Lightbulb className="w-3.5 h-3.5" />— Out-of-the-box ways to get it
              </div>
              <div className="bg-white border border-line rounded-sm divide-y divide-line">
                {(guide.out_of_box_routes || []).map((r, i) => (
                  <div key={i} className="px-5 py-4" data-testid={`land-route-${i}`}>
                    <div className="text-sm font-semibold text-stone_brand-dark mb-0.5">{i + 1}. {r.route}</div>
                    <p className="text-xs text-stone_brand mb-1">{r.how}</p>
                    <p className="text-[0.7rem] font-mono text-terracotta">Risk: {r.risk}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="grid md:grid-cols-2 gap-3">
              <section className="bg-white border border-line rounded-sm p-5">
                <div className="overline mb-2 flex items-center gap-1.5 text-moss">
                  <Droplets className="w-3.5 h-3.5" />— Verify water before you pay
                </div>
                <ul className="text-xs text-stone_brand space-y-1.5">
                  {(guide.water_checklist || []).map((c, i) => <li key={i}>☑ {c}</li>)}
                </ul>
              </section>
              <section className="bg-white border border-line rounded-sm p-5">
                <div className="overline mb-2 flex items-center gap-1.5 text-moss">
                  <ShieldCheck className="w-3.5 h-3.5" />— Legal checklist
                </div>
                <ul className="text-xs text-stone_brand space-y-1.5">
                  {(guide.legal_checklist || []).map((c, i) => <li key={i}>☑ {c}</li>)}
                </ul>
              </section>
            </div>

            <p className="text-[0.7rem] font-mono uppercase tracking-widest text-terracotta" data-testid="land-caution">
              ⚠ {guide.caution}
            </p>
          </div>
        )}

        {/* Always-visible next steps once user has submitted a location */}
        {(deals || guide || dealsLoading || loading) && (
          <div className="bg-moss-bg/50 border border-moss/30 rounded-sm p-5 flex flex-wrap items-center justify-between gap-3 mb-8" data-testid="land-next-steps">
            <div>
              <div className="font-serif-display text-lg text-stone_brand-dark font-medium flex items-center gap-2">
                <Handshake className="w-4 h-4 text-moss" /> Ready to move on a plot?
              </div>
              <p className="text-xs text-stone_brand mt-0.5">
                Follow the assisted checklist (documents to demand from the seller included) — or let us connect you with vetted sellers, free.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setWizard(true)} className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-10 gap-1.5" data-testid="open-land-wizard-btn">
                <Compass className="w-4 h-4" /> Assisted purchase — step by step
              </Button>
              <Button onClick={sendLead} disabled={leadSending} variant="outline" className="rounded-sm border-moss text-moss hover:bg-moss-bg h-10 gap-1.5" data-testid="land-connect-btn">
                <Handshake className="w-4 h-4" /> {leadSending ? "Sending…" : "Connect me with sellers"}
              </Button>
            </div>
          </div>
        )}

        {!guide && !loading && (
          <div className="bg-white border border-line rounded-sm p-12 text-center" data-testid="land-empty">
            <Sprout className="w-8 h-8 text-stone_brand-light mx-auto mb-4" />
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">Where do you want land?</div>
            <p className="text-sm text-stone_brand max-w-md mx-auto mb-4">
              Type any city, district or country above — or tap &ldquo;Use my location&rdquo; — and we&apos;ll research
              the cheapest water-rich farmland options around it.
            </p>
            <Button onClick={() => setWizard(true)} variant="outline" className="rounded-sm border-moss text-moss hover:bg-moss-bg gap-1.5" data-testid="open-land-wizard-empty-btn">
              <Compass className="w-4 h-4" /> See the step-by-step buying checklist
            </Button>
            <div className="mt-8 text-left">
              <div className="overline mb-3 text-moss text-center">— Trending farmland regions · tap to explore</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="featured-regions">
                {FEATURED_REGIONS.map((f, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setLocation(f.query);
                      fetchGuide({ location: f.query });
                    }}
                    className="bg-bone-100/60 border border-line rounded-sm p-4 text-left hover:border-moss/60 hover:bg-moss-bg/30 transition-colors"
                    data-testid={`featured-region-${i}`}
                  >
                    <div className="text-sm font-semibold text-stone_brand-dark">{f.label}</div>
                    <div className="text-[0.7rem] font-mono text-stone_brand mt-0.5">{f.tag}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      {wizard && <PurchaseWizard type="land" onClose={() => setWizard(false)} />}
    </div>
  );
}
